import { useCallback, useEffect, useMemo, useState } from 'react';
import { CPDService } from '../../../../services//cpd.service';
import { useSession } from '../../../../hooks/use-session.hook';
import { usePermissions } from '../../../../hooks/use-permissions.hook';
import { PROFILES } from '../../../../constants';
import { AlertService } from '../../../../services/alert.service';
import {
  FilterDef,
  FilterOption,
  FilterValues
} from '../../../../components/chip-filters/chip-filters.types';
import useRequest from '../../../../hooks/use-request.hook';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Icon from '../../../../components/forms/icon.component';
import Aside from '../../../../components/layout/asaid.component';
import Header from '../../../../components/layout/header.component';
import Footer from '../../../../components/layout/footer.component';
import FilterBar from '../../../../components/chip-filters/filter-bar.component';
import ModuleStatsHeader from '../../../../components/cards/header-stats.component';
import TableCPDTemplates from './table-templates';
import AddTemplateModal from './modal/templates-registers.modal';

const CHANNEL_LABELS: Record<string, string> = { energy: 'Energy', tone: 'Tone Spec' };

const CPDTemplates: React.FC = () => {
  useBgPages();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const { isAdmin, can } = usePermissions();
  const canWrite = can('cpd.write');
  const isRoot = session?.profile === PROFILES.SUPER_ADMIN;

  const [campaignMetrics, setCampaignMetrics] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [selectedChannel, setSelectedChannel] = useState<string>('');

  const [showModal, setShowModal] = useState<{ show: boolean; data: any }>({
    show: false,
    data: null
  });

  const campaignRows = useMemo<any[]>(() => {
    if (!Array.isArray(campaignMetrics)) return [];
    return campaignMetrics.map((item: any) => ({
      id: item.id,
      name: item.name,
      channel: item.config?.mode ?? 'unknown',
      mode: item.config?.mode ?? '',
      action: item.config?.action ?? '',
      maxAnalysisMs: item.config?.thresholds?.maxAnalysisMs ?? 0,
      minEnergyDbfs: item.config?.thresholds?.minEnergyDbfs ?? 0,
      minVoiceMs: item.config?.thresholds?.minVoiceMs ?? 0,
      maxSilenceMs: item.config?.thresholds?.maxSilenceMs ?? 0,
      toneSpec: item.config?.toneSpec ?? '—',
      toneCarrier: item.config?.toneCarrier ?? 'GENÉRICO',
      createdAt: item.createdAt ?? '',
      updatedAt: item.updatedAt ?? '',
      description: item.description ?? '',
      company: item.config?.toneCarrier ?? 'GENÉRICO',
      actionConfig: item.config?.action ?? '',
      account: item.config?.mode?.toUpperCase() ?? '',
      userUsername: '',
      userFullName: '',
      config: item.config
    }));
  }, [campaignMetrics]);

  const channelOptions = useMemo(() => {
    const seen = new Set<string>();
    return campaignRows
      .filter((r) => {
        const key = r.channel.toUpperCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((r) => ({
        value: r.channel.toUpperCase(),
        label: CHANNEL_LABELS[r.channel] ?? r.channel,
        iconPath: undefined
      }));
  }, [campaignRows]);

  const rowsByChannel = useMemo(
    () =>
      selectedChannel
        ? campaignRows.filter((r) => r.channel.toUpperCase() === selectedChannel)
        : campaignRows,
    [campaignRows, selectedChannel]
  );

  const companyOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return rowsByChannel.reduce<FilterOption[]>((acc, r) => {
      if (r.company && !seen.has(r.company)) {
        seen.add(r.company);
        acc.push({ value: r.company, label: r.company.toUpperCase() });
      }
      return acc;
    }, []);
  }, [rowsByChannel]);

  const actionOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return rowsByChannel.reduce<FilterOption[]>((acc, r) => {
      if (r.actionConfig && !seen.has(r.actionConfig)) {
        seen.add(r.actionConfig);
        acc.push({ value: r.actionConfig, label: r.actionConfig });
      }
      return acc;
    }, []);
  }, [rowsByChannel]);

  const filterDefs = useMemo<FilterDef[]>(() => {
    const defs: FilterDef[] = [];

    if (companyOptions.length > 0) {
      defs.push({
        key: 'company',
        label: 'Carrier',
        icon: <Icon icon="ki-office-bag" size="fs-7" />,
        options: companyOptions,
        multi: false,
        searchable: true
      });
    }

    if (actionOptions.length > 0) {
      defs.push({
        key: 'action',
        label: 'Acción',
        icon: <Icon icon="ki-setting-3" size="fs-7" />,
        options: actionOptions,
        multi: false,
        searchable: true
      });
    }

    return defs;
  }, [companyOptions, actionOptions]);

  const displayRows = useMemo(() => {
    let rows = rowsByChannel;
    if (filters.company) {
      rows = rows.filter((r) => r.company === filters.company);
    }
    if (filters.action) {
      rows = rows.filter((r) => r.actionConfig === filters.action);
    }

    return [...rows].sort((a, b) => b.maxAnalysisMs - a.maxAnalysisMs);
  }, [rowsByChannel, filters]);

  const handleFilterChange = useCallback((next: FilterValues) => {
    setFilters(next);
  }, []);

  const handleChannelChange = useCallback((channel: string) => {
    setSelectedChannel(channel);
    setFilters({});
  }, []);

  useEffect(() => {
    if (channelOptions.length === 0) {
      setSelectedChannel('');
      return;
    }
    const stillValid = channelOptions.some((o) => o.value === selectedChannel);
    if (!stillValid) {
      setSelectedChannel(channelOptions[0].value);
      setFilters({});
    }
  }, [channelOptions, selectedChannel]);

  const getTemplates = useCallback(async () => {
    try {
      const response = await call(() => CPDService.getTemplates());
      setCampaignMetrics(response);
    } catch (error: any) {
      AlertService.toastError(error?.error || error);
    }
  }, [call]);

  const handleDeleteTemplate = useCallback(
    async (id: string | number) => {
      try {
        const deletePromise = CPDService.deleteTemplates(String(id));

        await AlertService.toastLoading(
          deletePromise,
          'Eliminando registro...',
          'Registro eliminado exitosamente.',
          'Ocurrió un error al intentar eliminar el registro.'
        );
        getTemplates();
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
      }
    },
    [getTemplates]
  );

  const handleShowModal = useCallback(
    async (refresh: boolean) => {
      if (refresh) await getTemplates();
      setShowModal({ show: false, data: null });
    },
    [getTemplates]
  );

  const handleRefresh = useCallback(async () => {
    await getTemplates();
    AlertService.toastSuccess('¡Éxito!, información actualizada correctamente.');
  }, [getTemplates]);

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <ModuleStatsHeader
                stats={[
                  {
                    label: 'Configuraciones Activas',
                    value: displayRows.length,
                    icon: 'ki-duotone ki-chart fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  },
                  {
                    label: 'Modo Seleccionado',
                    value: selectedChannel || 'TODOS',
                    icon: 'ki-duotone ki-send fs-1 text-info',
                    bgColor: 'bg-light',
                    textColor: 'text-dark'
                  }
                ]}
                onRefresh={handleRefresh}
                isLoading={isRequesting}
                extraActions={
                  canWrite ? (
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                      onClick={() => setShowModal({ show: true, data: null })}
                    >
                      <Icon icon="ki-plus" size="fs-3" />
                      Agregar Template
                    </button>
                  ) : undefined
                }
              />

              {campaignRows.length > 0 && (
                <FilterBar
                  className="mt-4"
                  defs={filterDefs}
                  value={filters}
                  onChange={handleFilterChange}
                  channelOptions={channelOptions}
                  selectedChannel={selectedChannel}
                  onChannelChange={handleChannelChange}
                />
              )}

              <div className="card shadow-sm border-0 rounded-4 mt-4 mt-md-6 overflow-hidden">
                <div className="card-body p-4 p-md-6">
                  <TableCPDTemplates
                    rows={displayRows}
                    isLoading={isRequesting}
                    isRoot={isRoot}
                    isAdmin={isAdmin}
                    onDelete={handleDeleteTemplate}
                    onEdit={(row: any) => setShowModal({ show: true, data: row })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showModal.show && (
        <AddTemplateModal
          show={showModal.show}
          onRequestClose={(refresh: any) => handleShowModal(refresh)}
          payload={showModal.data}
        />
      )}
    </div>
  );
};

export default CPDTemplates;
